import type { GridFilterItem, GridFilterOperator } from "@mui/x-data-grid";

/**
 * Filtro de coluna para CPF e CNPJ na DataGrid que ignora a máscara.
 *
 * Por que existe: nas telas de edição a coluna mostra o documento formatado
 * (`valueFormatter` com `formatCnpj`), mas o valor da linha fica cru, porque
 * é comparado com o dado original e vai no payload. O filtro de coluna da
 * DataGrid 8 compara com o valor cru (`getRowValue`), então quem digitava o
 * que vê na tela (`12.345.678`) recebia zero linhas.
 *
 * Como funciona: quando a busca tem pelo menos um dígito, os dois lados
 * perdem tudo o que não é dígito antes de chamar o operador original. Busca
 * sem dígito (ex. `remover`) e os operadores sem valor (vazio, não vazio)
 * seguem o comportamento padrão. Não usar `valueGetter` no lugar disto: ele
 * muda o valor da linha, e com ele o payload.
 *
 * Uso: `filterOperators: ignoreFederalTaxIdMask(getGridStringOperators())`.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyOperator = GridFilterOperator<any, any, any>;

const hasDigit = (value: unknown): boolean => typeof value === "string" && /\d/.test(value);

const onlyDigits = (value: string): string => value.replace(/\D/g, "");

function normalizeFilterValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map((v) => (typeof v === "string" ? onlyDigits(v) : v));
  return typeof value === "string" ? onlyDigits(value) : value;
}

export function ignoreFederalTaxIdMask(operators: AnyOperator[]): AnyOperator[] {
  return operators.map((operator) => ({
    ...operator,
    getApplyFilterFn: (filterItem: GridFilterItem, column) => {
      const searched = Array.isArray(filterItem.value) ? filterItem.value : [filterItem.value];
      if (!searched.some(hasDigit)) return operator.getApplyFilterFn(filterItem, column);

      const apply = operator.getApplyFilterFn({ ...filterItem, value: normalizeFilterValue(filterItem.value) }, column);
      if (!apply) return null;
      return (value, row, col, apiRef) =>
        apply(value == null ? value : onlyDigits(String(value)), row, col, apiRef);
    },
  }));
}
